/**
 * Configurable analytics wrapper with dataLayer fallback.
 *
 * By default, uses dataLayer.push() directly for widget builds.
 * React/Next apps can call configureAnalytics({ useAnalyticsLibrary: true })
 * to enable the @gartner-digital-markets/analytics library.
 */

type EventParams = Record<string, unknown>;
type SendEventFn = (params: EventParams) => void;

interface AnalyticsConfig {
  useAnalyticsLibrary?: boolean;
}

let config: AnalyticsConfig = {
  useAnalyticsLibrary: true, // Default to analytics library for React/Next apps
};

let resolvedSendEvent: SendEventFn | null = null;
let loadState: 'idle' | 'loading' | 'resolved' = 'idle';
let pendingEvents: EventParams[] = [];

/**
 * Configure the analytics behavior.
 * Call this before mounting any forms to enable the analytics library.
 */
export const configureAnalytics = (newConfig: AnalyticsConfig) => {
  config = { ...config, ...newConfig };
  // Reset state if config changes
  if (loadState !== 'idle') {
    resolvedSendEvent = null;
    loadState = 'idle';
  }
};

// Push directly to dataLayer
const pushToDataLayer: SendEventFn = (params) => {
  if (typeof window !== 'undefined') {
    // biome-ignore lint/suspicious/noExplicitAny: dataLayer is a global GTM array
    const windowWithDataLayer = window as any;
    windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];
    const dataLayer = windowWithDataLayer.dataLayer;
    // Kept as 'gdm_forms_event' for backward compatibility — external consumers filter GTM triggers on this string.
    dataLayer.push({ event: params.name || 'gdm_forms_event', ...params });
  }
};

/**
 * Send an analytics event.
 * Uses dataLayer by default, or the analytics library if configured.
 */
export const sendEvent: SendEventFn = (params) => {
  // If not using analytics library, always push to dataLayer directly
  if (!config.useAnalyticsLibrary) {
    pushToDataLayer(params);
    return;
  }

  // Fast path: already resolved
  if (loadState === 'resolved' && resolvedSendEvent) {
    resolvedSendEvent(params);
    return;
  }

  // Queue the event
  pendingEvents.push(params);

  // Start loading if idle
  if (loadState === 'idle') {
    loadState = 'loading';

    import('@gartner-digital-markets/analytics')
      .then((analytics) => {
        resolvedSendEvent = analytics.sendEvent as unknown as SendEventFn;
        loadState = 'resolved';
        const eventsToFlush = pendingEvents;
        pendingEvents = [];
        for (const event of eventsToFlush) {
          resolvedSendEvent(event);
        }
      })
      .catch((error) => {
        if (typeof console !== 'undefined' && console.warn) {
          // biome-ignore lint/suspicious/noConsole: missing analytics should surface during debugging
          console.warn(
            '[bx-forms] Analytics library not available, using dataLayer fallback:',
            error
          );
        }
        resolvedSendEvent = pushToDataLayer;
        loadState = 'resolved';
        const eventsToFlush = pendingEvents;
        pendingEvents = [];
        for (const event of eventsToFlush) {
          pushToDataLayer(event);
        }
      });
  }
};
